import Cookies from "universal-cookie";
import { clearWalletCache } from "./helper/wallet-helper";
import { getUserContract } from "./helper/contract-helper";
import { deleteContacts, deleteDatabase } from "./db/db-helper";
import { buildLoginProof } from "./auth/loginProof.js";
import { login } from "./service/api-actions.js";
import { Bounce, toast } from "react-toastify";
import Loader from './components/Loader';
import { useState } from "react";
import { clearContactsDekCache } from "./helper/contactsDek";
import { clearDekCache } from "./helper/dekManager";
import { clearEnvelopeKeyCache } from "./helper/envelopeV3";
const cookies = new Cookies();

/**
 * True when a failed login attempt is a TRANSIENT infrastructure problem (rate limit, network drop, server
 * error) rather than the server rejecting these credentials. Those must never trigger logout(): the session
 * is still valid, the request just didn't get through. fetchAPI returns { __error: true, status, message }
 * for thrown requests, so the HTTP status is available here.
 */
function isTransientFailure(result) {
    if (!result) { return true; }                       // no response at all — treat as transient, not a rejection
    if (result.__error !== true) { return false; }      // a real auth verdict from the server
    const status = result.status;
    return status === null || status === undefined || status === 429 || status >= 500;
}

/** How long a best-effort wipe may hold a redirect before we give up on it and navigate anyway. */
const WIPE_TIMEOUT_MS = 2000;

/**
 * Runs a storage wipe so that it can never take a redirect down with it.
 *
 * deleteContacts() (db/db-helper.js) has no try/catch of its own, unlike deleteDatabase(), and a Dexie
 * open can BOTH reject (IndexedDB unavailable in a hardened or private-mode context) and NEVER SETTLE (a
 * version upgrade blocked by another tab still holding the DB open — routine for webmail, where a second
 * tab is the norm). requireAuth() is a React Router loader, so both failures are fatal to the redirect:
 * a rejected loader routes to an errorElement index.js mounts on none of these routes, and a loader that
 * never settles leaves the visitor on the loading state forever. Neither ever reaches connect-wallet.
 *
 * Hence: swallow the failure, and bound the wait instead of dropping it — an unawaited delete can be cut
 * off by the navigation before IndexedDB commits it, so the cleanup still gets its chance, it just does
 * not get a veto.
 *
 * ONLY for cleanups the redirect must not wait on. The account-switch deleteDatabase() below is NOT one
 * of them: it has to finish before the incoming account can reach the cache.
 */
function bestEffortWipe(wipe, label) {
    return Promise.race([
        Promise.resolve().then(wipe).catch((err) => console.warn(`${label} failed — continuing.`, err)),
        new Promise((resolve) => setTimeout(resolve, WIPE_TIMEOUT_MS)),
    ]);
}

export async function requireAuth() {

    const user = cookies.get("userObject");
    if (user && user.name) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        // Every `return` in this function returns a VALUE on purpose. requireAuth is a React Router loader,
        // and @remix-run/router asserts a loader returned something ("...but didn't return anything from
        // your `loader` function"); returning undefined turns into a router error, and index.js mounts no
        // errorElement on these routes — so the default error page can flash in the gap between
        // window.open() and the navigation actually committing. Nothing reads the value (no useLoaderData
        // anywhere), so `true` is only there to satisfy the invariant.
        if (!window.ncogProvider) {
            window.open('/app', '_self');
            return true;
        }
        const address = localStorage.getItem("walletAddress");
        if (!address) {
            window.open('/app', '_self');
            return true;
        }

        try {
            if (address) {
                // The chain read is fenced off on its own because a failed read is NOT an auth verdict.
                // getUserContract() swallows a provider-construction failure and returns undefined, and
                // contractCall() returns null, so an RPC blip reaches this line as a TypeError exactly like
                // a 429 or a 502 does — and the outer catch fell straight through to the logout() at the
                // bottom of this function, deleting the decrypted mail cache the wallet paid one prompt per
                // message to build (db/db-helper.js keeps that cache across sign-ins on purpose). Same rule
                // isTransientFailure() applies to login(): we could not check, so we change nothing and let
                // the next load re-check.
                let getUserDetailsForWallet;
                try {
                    const contract = getUserContract();
                    getUserDetailsForWallet = await contract.methods.getUserDetailsForWallet(address).call();
                } catch (chainError) {
                    console.warn('Could not read the wallet accounts from the chain — leaving the session alone.', chainError);
                    // requireAuth() is the loader for every in-app route, so a fixed toastId keeps an
                    // outage to ONE message instead of one per navigation.
                    toast.error('Network is busy — some details may be out of date.', {
                        toastId: 'requireAuthChainRead',
                        position: "top-center",
                        transition: Bounce,
                        autoClose: 5000,
                    });
                    return true;
                }

                if (getUserDetailsForWallet && getUserDetailsForWallet.length) {

                    const userName = user.name;
                    const userPresent = getUserDetailsForWallet.some(userData => userData.userName === userName);

                    if (!userPresent) {
                        // A DIFFERENT WALLET IS NOT AN INVITATION TO PICK AN ACCOUNT FOR THE USER.
                        //
                        // This branch used to take getUserDetailsForWallet[0] unconditionally and sign in as
                        // it — minting a token, overwriting the userObject cookie and redirecting to the
                        // inbox. requireAuth() is the router loader for EVERY in-app route, so it ran on
                        // every navigation. The DAuth proof-of-possession SUCCEEDS for that account (the
                        // wallet really does own it), so the server is not a backstop: the user is silently
                        // switched to a different identity, and then sends mail as, and decrypts mail for,
                        // someone they did not choose. Wallets owning two accounts are normal here, which
                        // made this a coin flip rather than an edge case.
                        //
                        // Only an UNAMBIGUOUS answer may be taken automatically. Anything else goes to the
                        // account chooser (routes/SignIn.js), which already lists the wallet's accounts and
                        // signs in as the one the user picks. Same rule the mobile client's session ladder
                        // follows: confirm or ask, never guess.
                        if (getUserDetailsForWallet.length > 1) {
                            console.warn(
                                `Session user is not on the connected wallet, which owns ${getUserDetailsForWallet.length} accounts. ` +
                                'Sending the user to the account chooser rather than picking one.'
                            );
                            window.open('/app/signin', '_self');
                            return true;
                        }

                        const userFirstData = getUserDetailsForWallet[0];
                        // Wallet proof-of-possession first, then the login it authorises. buildLoginProof
                        // throws an already user-facing message; folding it into the same auth-shaped
                        // failure object login() returns keeps the toast-and-logout branch below as the
                        // single place this screen reports a failed sign-in.
                        let getToken;
                        try {
                            const { signature, signerPublicKey } = await buildLoginProof(userFirstData.userName, userFirstData.publicKey);
                            getToken = await login(userFirstData.userName, userFirstData.publicKey, null, signature, signerPublicKey);
                        } catch (proofError) {
                            getToken = { isAuth: false, isAccess: false, message: proofError.message };
                        }
                        if (getToken && getToken.isAuth && getToken.token) {
                            const userObject = { name: userFirstData.userName, wallet: userFirstData.walletAddress, publicKey: userFirstData.publicKey };
                            cookies.set("accessToken", getToken.token, { path: "/" });
                            localStorage.setItem("accessToken", getToken.token);
                            cookies.set("userObject", JSON.stringify(userObject), { path: "/" });
                            // A genuine account switch (the session's user is not on this wallet), so the
                            // cache must go — and the in-memory DEKs with it, or the incoming account
                            // would reuse the previous account's keys.
                            clearDekCache();
                            clearContactsDekCache();
                            // The v3 content keys too: they are per-message keys unwrapped by the
                            // OUTGOING account's wallet, so leaving them behind would let the incoming
                            // account open that mail with no wallet call of its own.
                            clearEnvelopeKeyCache();
                            await deleteDatabase();
                            window.open('/app/inbox', '_self');
                        } else if (isTransientFailure(getToken)) {
                            // Rate limit / network / server blip — NOT a rejected sign-in. Logging out here
                            // is what threw users back to connect-wallet on a momentary 429. Report it and
                            // leave the session alone so a reload can recover.
                            toast.error(getToken?.message || 'Network is busy — please try again in a moment.', {
                                position: "top-center",
                                transition: Bounce,
                                autoClose: 5000,
                            });
                        } else if (!getToken?.isAccess && !getToken.isAuth) {
                            toast.error(getToken?.message, {
                                position: "top-center",
                                transition: Bounce,
                                autoClose: 5000,
                            });
                            logout();
                        } else {
                            window.open('/app/signup', '_self');
                        }
                    } else {

                    }
                } else if (!getUserDetailsForWallet) {
                    // The RPC answered with nothing at all rather than throwing — still "we could not
                    // check", and logging out here wiped the mail cache on a blip just as surely.
                    console.warn('The chain returned no account list for this wallet — leaving the session alone.');
                    return true;
                } else {
                    // An EMPTY list contradicts a live session: accounts are never removed from the
                    // registry, so infrastructure is a likelier explanation than a revoked account. The
                    // account chooser re-reads the chain itself and can offer signup if the wallet really
                    // owns nothing, and its checkAuth() drops the stale session WITHOUT deleting the
                    // decrypted mail — which is the part logout() makes the user pay for all over again.
                    console.warn('The connected wallet reports no accounts — sending the user to the account chooser.');
                    window.open('/app/signin', '_self');
                    return true;
                }
            } else {
                logout();
            }
            return true;
        } catch (err) {
            // Anything unexpected above is still not proof that this wallet is unauthorised. Returning
            // leaves the session and the cache alone — control used to fall out of the try/catch into an
            // unconditional logout(), which turned a single flaky chain response into a full cache-and-
            // session wipe. The value is also what the loader invariant requires (see the top of requireAuth).
            console.log(err);
            return true;
        }
    }
    else {
        // NO SESSION IS NOT A SIGN-OUT, AND IT IS NOT AN ACCOUNT SWITCH.
        //
        // Both auth cookies are set without maxAge/expires, i.e. SESSION cookies, so simply CLOSING THE
        // BROWSER lands the next visit here. This branch used to fall through to the logout() at the bottom
        // of the function, whose deleteDatabase() threw away the decrypted mail cache — the one db-helper.js
        // deliberately keeps across sign-ins, and that the wallet paid one prompt per message to build. The
        // result was a full re-decrypt of the mailbox on every browser restart.
        //
        // Which case is which:
        //  - MISSING SESSION (here): the visitor is simply not signed in. Redirecting to connect-wallet is
        //    correct; destroying their data is not. What stays on disk is the mail cache, and it is
        //    ciphertext under that account's dMail DEK (db/indexed-helper.js) — unreadable without the
        //    wallet that owns it.
        //  - ACCOUNT CHANGE: still wiped, and not from here. routes/SignIn.js and routes/WalletConnect.js
        //    compare getCacheOwner() with the account signing in and wipe everything (mail DB, contacts DB,
        //    in-memory DEKs) whenever it differs — including when the owner is unknown (null), so a
        //    pre-owner-tag cache is never served to the wrong account. That is the invariant, and it is
        //    untouched.
        //  - EXPLICIT SIGN-OUT: still logout() below, unchanged.
        //
        // Two things do NOT survive a missing session. The stale bearer token is a credential, not a cache,
        // so it goes; and the 'Dcontact' rows are the one store held in PLAINTEXT at rest, so they go too —
        // free of charge, because sign-in re-primes the contacts DEK and re-fetches the list every time
        // regardless of what is cached (routes/SignIn.js).
        //
        // Expired the way the rest of the app expires a path-"/" cookie (auth/logout.js, routes/SignIn.js):
        // a bare cookies.remove() is scoped to the current page's path, which is never "/" under the /app
        // basename, so the cookie would survive.
        cookies.set("accessToken", '', { path: "/", maxAge: 0 });
        localStorage.removeItem("accessToken");
        // The credential above is gone synchronously; the contact rows are a cleanup, and a cleanup is
        // never allowed to decide whether the visitor gets redirected — see bestEffortWipe().
        await bestEffortWipe(deleteContacts, 'Clearing the cached contacts');
        window.open('/app', '_self');
        return true;
    }
}

export async function logout() {
    // CREDENTIALS FIRST, SYNCHRONOUSLY, BEFORE ANY await.
    //
    // This was `cookies.remove("accessToken")` with no options, which under the "/app" basename
    // (index.js) scopes the removal to the current document's path — never "/" — so a cookie set with
    // { path: "/" } (this file, routes/SignIn.js, routes/WalletConnect.js, routes/Signup.js) was NOT
    // removed. And userObject, the cookie requireAuth() actually gates the whole session on, was never
    // touched at all. Expired here the way the rest of the app expires a path-"/" cookie
    // (auth/logout.js, routes/SignIn.js). logout() is the only full-wipe path left, so it has to
    // actually log the user out rather than rely on localStorage.clear() below tripping requireAuth's
    // !address guard.
    //
    // Ordering matters for the same reason: everything below is awaited, and an IndexedDB delete can be
    // blocked indefinitely by another open tab. Clearing the credentials and the in-memory keys up here
    // means a stalled — or failed — cache wipe can no longer leave a working session, or the previous
    // user's decrypted keys, behind.
    cookies.set("accessToken", '', { path: "/", maxAge: 0 });
    cookies.set("userObject", '', { path: "/", maxAge: 0 });
    localStorage.clear();
    clearWalletCache();
    // Drop the in-memory DEKs too, so the next user cannot inherit the previous user's keys.
    clearContactsDekCache();
    clearDekCache();
    // ...and the v3 per-message content keys, for the same reason.
    clearEnvelopeKeyCache();
    // Logout is a real wipe and should complete before we navigate: the mail cache now survives sign-in,
    // so anything left behind here would still be sitting there for whoever signs in next. Bounded and
    // failure-swallowing all the same — deleteContacts() has no try/catch of its own, and its rejection
    // used to abort logout() outright, skipping the key clears above and the redirect below.
    await bestEffortWipe(deleteDatabase, 'Deleting the mail cache');
    await bestEffortWipe(deleteContacts, 'Deleting the cached contacts');
    window.open('/app', '_self');
}

